import type { SafetyWindow } from '@magicschool/business-logic';
import { logger } from '@magicschool/logger';
import type { Enums, Tables } from '@magicschool/supabase/types';
import { parseISO } from 'date-fns';
import type { RoomSchedulingStatus } from '../types';
import { getNextSafetyWindowTransition, isSafetyWindowOpen, type SafetyWindowErrorMode, toHHmm } from './safetyWindow';

/**
 * Determines the current scheduling status of a room based on the current time and room configuration
 * @param now - The current date/time to compare against
 * @param room - The room object containing scheduling information
 * @returns The current scheduling status of the room
 */
export const determineSchedulingStatus = (now: Date, room: Partial<Tables<'rooms'>>): RoomSchedulingStatus => {
  if (!room.scheduling_enabled) {
    return 'none';
  }

  const startAt = room.scheduled_start_at ? parseISO(room.scheduled_start_at) : null;
  const endAt = room.scheduled_end_at ? parseISO(room.scheduled_end_at) : null;

  // Validate that start time is before end time if both are provided
  if (startAt && endAt && startAt >= endAt) {
    logger.warn('Invalid room schedule: start time must be before end time', {
      roomId: room.id,
      startAt: room.scheduled_start_at,
      endAt: room.scheduled_end_at,
    });
    return 'none';
  }

  if (startAt && now < startAt) {
    return 'not-started';
  }

  if (endAt && now > endAt) {
    return 'ended';
  }

  return 'active';
};

/**
 * Determines the actual room state based on scheduling status and current state
 * @param schedulingStatus - The current scheduling status of the room
 * @param currentState - The current state of the room
 * @returns The computed room state that should be used
 */
export const determineStatusBasedOnScheduling = (
  schedulingStatus: RoomSchedulingStatus,
  currentState: Enums<'room_state'> | undefined,
): Enums<'room_state'> => {
  // Archived rooms maintain their state regardless of scheduling
  if (currentState === 'archived') {
    return currentState;
  }

  // If scheduling is disabled, always use the current state
  if (schedulingStatus === 'none') {
    return currentState || 'locked';
  }

  // If room is scheduled but not started or has ended, force locked state
  // This overrides any realtime state changes from teachers
  if (schedulingStatus === 'not-started' || schedulingStatus === 'ended') {
    return 'locked';
  }

  // If room is actively scheduled, allow the current state
  // This allows teachers to change state during active periods
  return currentState || 'active';
};

/** Every column the evaluator reads. Selecting fewer is a type error, so no layer can be skipped by omission. */
export type RoomAvailabilityRow = Pick<
  Tables<'rooms'>,
  | 'state'
  | 'scheduling_enabled'
  | 'scheduled_start_at'
  | 'scheduled_end_at'
  | 'open_days'
  | 'open_start_time'
  | 'open_end_time'
  | 'timezone'
>;

export const ROOM_AVAILABILITY_COLUMNS =
  'state, scheduling_enabled, scheduled_start_at, scheduled_end_at, open_days, open_start_time, open_end_time, timezone' as const;

export type DetermineRoomStatusOptions = {
  /** The creator org's window; pass null only when the caller knows there is none or is display-only. */
  safetyWindow: SafetyWindow | null;
  onError?: SafetyWindowErrorMode;
};

/** The room's own recurring window; null when unrestricted or when scheduling is off. */
export const getRoomWindow = (room: RoomAvailabilityRow): SafetyWindow | null => {
  if (!room.scheduling_enabled) return null;
  if (room.open_days == null && room.open_start_time == null) return null;
  return {
    startsAt: null,
    endsAt: null,
    openDays: room.open_days ?? null,
    openStartTime: room.open_start_time == null ? null : toHHmm(room.open_start_time),
    openEndTime: room.open_end_time == null ? null : toHHmm(room.open_end_time),
    timezone: room.timezone ?? null,
  };
};

export const determineRoomStatus = (
  now: Date,
  room: RoomAvailabilityRow,
  { safetyWindow, onError }: DetermineRoomStatusOptions,
): Enums<'room_state'> => {
  const state = determineStatusBasedOnScheduling(determineSchedulingStatus(now, room), room.state);
  if (state === 'archived') return state;
  const windows = [safetyWindow, getRoomWindow(room)].filter((window): window is SafetyWindow => window !== null);
  return windows.every((window) => isSafetyWindowOpen(now, window, { onError })) ? state : 'locked';
};

/** Least to most restrictive. */
const STATE_RESTRICTIVENESS: Enums<'room_state'>[] = ['active', 'paused', 'locked', 'archived'];

export type StudentRoomStatusRow = Pick<Tables<'rooms'>, 'state' | 'scheduling_enabled' | 'scheduled_start_at' | 'scheduled_end_at'>;

/**
 * One student's status in a room: the more restrictive of the room's status (its state and date schedule) and the
 * student's own state, since student access is denied when either one denies it. Pausing or locking a room never
 * updates its students' rows, so neither state alone is enough. Hours are left out, as the room's own status
 * already explains them.
 */
export const determineStudentRoomStatus = (
  now: Date,
  room: StudentRoomStatusRow,
  studentState: Enums<'room_state'>,
): Enums<'room_state'> => {
  const roomStatus = determineStatusBasedOnScheduling(determineSchedulingStatus(now, room), room.state);
  const roomRank = STATE_RESTRICTIVENESS.indexOf(roomStatus);
  const studentRank = STATE_RESTRICTIVENESS.indexOf(studentState);
  return roomRank >= studentRank ? roomStatus : studentState;
};

export type ClosingHours = { by: 'organization' | 'room'; window: SafetyWindow };

/** The hours closing the room right now, organization first, regardless of the room's own state; null when every window is open. */
export const getClosingHours = (
  now: Date,
  room: RoomAvailabilityRow,
  { safetyWindow, onError }: DetermineRoomStatusOptions,
): ClosingHours | null => {
  if (safetyWindow && !isSafetyWindowOpen(now, safetyWindow, { onError })) return { by: 'organization', window: safetyWindow };
  const roomWindow = getRoomWindow(room);
  if (roomWindow && !isSafetyWindowOpen(now, roomWindow, { onError })) return { by: 'room', window: roomWindow };
  return null;
};

const MAX_OPENING_STEPS = 16;

/** The next instant students can use the room, or null when it depends on a teacher or can't be found within about a week. */
export const getNextRoomOpening = (
  now: Date,
  room: RoomAvailabilityRow,
  { safetyWindow, onError }: DetermineRoomStatusOptions,
): Date | null => {
  const windows = [safetyWindow, getRoomWindow(room)].filter((window): window is SafetyWindow => window !== null);
  let at = now;
  for (let step = 0; step < MAX_OPENING_STEPS; step++) {
    const transitions = windows.map((window) => getNextSafetyWindowTransition(at, window)).filter((date): date is Date => date !== null);
    if (transitions.length === 0) return null;
    at = new Date(Math.min(...transitions.map((date) => date.getTime())));
    if (determineRoomStatus(at, room, { safetyWindow, onError }) === 'active') return at;
  }
  return null;
};

/** The creator org's hours as loaded on the server; a failure is kept so display and enforcement can each apply their policy. */
export type OrgHours = { status: 'loaded'; window: SafetyWindow | null } | { status: 'load-failure' };

export const loadedOrgWindow = (orgHours: OrgHours): SafetyWindow | null => (orgHours.status === 'loaded' ? orgHours.window : null);

/** `unknown` when the org hours failed to load: enforcement fails closed, so the room is closed for a reason display can't name. */
export type HoursOnlyClosure = (ClosingHours & { opensAt: Date | null }) | { by: 'unknown'; window: null; opensAt: null };

/**
 * For display: why hours keep an otherwise available room closed, and when it next opens. Null when the teacher's state or
 * the date schedule already closes it, or nothing does. Unparseable windows read as open.
 */
export const getHoursOnlyClosure = (
  now: Date,
  room: RoomAvailabilityRow,
  orgHours: OrgHours,
  closing: ClosingHours | null = getClosingHours(now, room, { safetyWindow: loadedOrgWindow(orgHours), onError: 'open' }),
): HoursOnlyClosure | null => {
  if (determineStatusBasedOnScheduling(determineSchedulingStatus(now, room), room.state) !== 'active') return null;
  if (orgHours.status === 'load-failure') return { by: 'unknown', window: null, opensAt: null };
  return closing && { ...closing, opensAt: getNextRoomOpening(now, room, { safetyWindow: orgHours.window, onError: 'open' }) };
};
